import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Alert,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import { DatePicker, TimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import { BookingSlotsConfig, Room } from '../../../models/Room';
import { GearList } from './GearList';
import { AdditionalGear, RequestedAdditionalGear } from '../../../models/AdditionalGear';
import { useThemeContext } from '../../../contexts/ThemeContext';
import type { TutorLessonOption } from '../../../models/Tutor';
import type { HostBookingClientOption } from '../../../models/HostOrganizationUser';
import BookingClientSelect from '../../../components/host/BookingClientSelect';
import TutorLessonOptionPicker from '../../../components/tutor/TutorLessonOptionPicker';
import {
  buildDurationOptionsMinutes,
  getEffectiveBookingSlotsConfig,
} from '../../../utils/bookingSlotsConfig';
import {
  DEFAULT_MANUAL_BOOKING_COUNTRY_CODE,
  DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH,
  isManualBookingNewClient,
} from '../../../utils/manualHostBooking';
import { tutorSessionNeedsRoom } from '../../../utils/tutorSessionType';
import BookingOccurrenceEditor from '../../../components/booking/BookingOccurrenceEditor';
import ManualBookingPriceSummary from '../../../components/booking/ManualBookingPriceSummary';
import { bookingSessionsOverlap } from '../../../utils/bookingRepeatSlots';
import type { DiscountSettings } from '../../../models/Organization';

export type HostCreateIntent = 'block' | 'booking';
export type HostCreateEventKind = 'room' | 'tutor';

export interface HostCreateEventTutorOption {
  tutorId: string;
  name: string;
}

interface BookingDialogProps {
  open: boolean;
  onClose: () => void;
  intent: HostCreateIntent;
  eventKind: HostCreateEventKind;
  setEventKind: (kind: HostCreateEventKind) => void;
  rooms: Room[];
  tutors: HostCreateEventTutorOption[];
  selectedTutorId: string;
  setSelectedTutorId: (tutorId: string) => void;
  students: HostBookingClientOption[];
  studentsLoading?: boolean;
  selectedStudentId: string;
  setSelectedStudentId: (userId: string) => void;
  guestPhoneLocal: string;
  setGuestPhoneLocal: (phone: string) => void;
  sendBookingLinkSms: boolean;
  setSendBookingLinkSms: (send: boolean) => void;
  tutorLessonOptions: TutorLessonOption[];
  /** Organization booking slot constraints (room create start/duration). */
  organizationBookingSlotsConfig?: BookingSlotsConfig | null;
  /** Selected tutor listing constraints (mirrored org config; for start-time step). */
  tutorBookingSlotsConfig?: BookingSlotsConfig | null;
  /** Space currency for room bookings. Tutor lessons prefer the lesson option currency. */
  currencyCode?: string | null;
  /** Organization single-booking discount, applied to the price shown before submit. */
  discountSettings?: DiscountSettings | null;
  tutorLessonOptionId: string;
  setTutorLessonOptionId: (id: string) => void;
  tutorTitle: string;
  setTutorTitle: (title: string) => void;
  selectedDate: Date | null;
  setSelectedDate: (date: Date | null) => void;
  selectedTime: Date | null;
  setSelectedTime: (time: Date | null) => void;
  extraSessionStarts: Date[];
  setExtraSessionStarts: (starts: Date[]) => void;
  selectedRoom: string;
  setSelectedRoom: (roomId: string) => void;
  bookingDuration: number;
  setBookingDuration: (duration: number) => void;
  isPastBooking: boolean;
  hasConflict: boolean;
  submitting?: boolean;
  handleCreateBooking: () => void;
  availableGears: AdditionalGear[];
  selectedAdditionalGears: RequestedAdditionalGear[];
  loadingGears: boolean;
  gearError: string | null;
  onToggleGear: (gear: AdditionalGear) => void;
}

const fieldSx = (mode: string) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : '#f8f9fa',
    '&:hover': {
      backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : '#f0f2ff',
    },
  },
});

export const BookingDialog: React.FC<BookingDialogProps> = ({
  open,
  onClose,
  intent,
  eventKind,
  setEventKind,
  rooms,
  tutors,
  selectedTutorId,
  setSelectedTutorId,
  students,
  studentsLoading = false,
  selectedStudentId,
  setSelectedStudentId,
  guestPhoneLocal,
  setGuestPhoneLocal,
  sendBookingLinkSms,
  setSendBookingLinkSms,
  tutorLessonOptions,
  organizationBookingSlotsConfig = null,
  tutorBookingSlotsConfig = null,
  currencyCode = null,
  discountSettings = null,
  tutorLessonOptionId,
  setTutorLessonOptionId,
  tutorTitle,
  setTutorTitle,
  selectedDate,
  setSelectedDate,
  selectedTime,
  setSelectedTime,
  extraSessionStarts,
  setExtraSessionStarts,
  selectedRoom,
  setSelectedRoom,
  bookingDuration,
  setBookingDuration,
  isPastBooking,
  hasConflict,
  submitting = false,
  handleCreateBooking,
  availableGears,
  selectedAdditionalGears,
  loadingGears,
  gearError,
  onToggleGear,
}) => {
  const { t } = useTranslation();
  const { mode } = useThemeContext();
  const sx = fieldSx(mode);
  const hasTutors = tutors.length > 0;
  const hasRooms = rooms.length > 0;
  const showEventKindToggle = hasTutors && hasRooms;
  const isBlock = intent === 'block';
  const roomDurationOptions = buildDurationOptionsMinutes(
    organizationBookingSlotsConfig,
    720,
  );
  const roomStartStepMinutes = getEffectiveBookingSlotsConfig(
    organizationBookingSlotsConfig,
  ).startTimeCardinalityInMinutes;
  const tutorStartStepMinutes = getEffectiveBookingSlotsConfig(
    tutorBookingSlotsConfig,
  ).startTimeCardinalityInMinutes;
  const startTimeMinutesStep =
    eventKind === 'room' ? roomStartStepMinutes : tutorStartStepMinutes;

  const getCurrentLocale = () => {
    const lang = t('calendar.locale', 'en');
    if (lang === 'pl') return plPL;
    return enGB;
  };

  const canSubmitRoomBlock =
    isBlock &&
    eventKind === 'room' &&
    Boolean(selectedRoom && selectedDate && selectedTime) &&
    !hasConflict &&
    !isPastBooking &&
    !submitting;

  const canSubmitTutorBlock =
    isBlock &&
    eventKind === 'tutor' &&
    Boolean(selectedTutorId && selectedDate && selectedTime) &&
    !hasConflict &&
    !isPastBooking &&
    !submitting;

  const primaryStart =
    selectedDate && selectedTime
      ? new Date(
          selectedDate.getFullYear(),
          selectedDate.getMonth(),
          selectedDate.getDate(),
          selectedTime.getHours(),
          selectedTime.getMinutes(),
          0,
          0,
        )
      : null;
  const selectedTutorLessonOption =
    tutorLessonOptions.find((option) => option.id === tutorLessonOptionId) ?? null;
  const sessionDurationMinutes =
    !isBlock && eventKind === 'tutor'
      ? selectedTutorLessonOption?.durationMinutes ?? 60
      : bookingDuration;
  const sessionStarts = !isBlock && primaryStart ? [primaryStart, ...extraSessionStarts] : [];
  const sessionsOverlap = bookingSessionsOverlap(
    sessionStarts.map((start) => ({ start, durationMinutes: sessionDurationMinutes })),
  );

  const applySessionStarts = (next: Date[]) => {
    if (next.length === 0) return;
    const [first, ...rest] = next;
    setSelectedDate(new Date(first.getFullYear(), first.getMonth(), first.getDate()));
    setSelectedTime(new Date(first));
    setExtraSessionStarts(rest);
  };

  const canSubmitRoomBooking =
    !isBlock &&
    eventKind === 'room' &&
    Boolean(selectedRoom && selectedDate && selectedTime && selectedStudentId) &&
    !hasConflict &&
    !isPastBooking &&
    !sessionsOverlap &&
    !submitting;

  const tutorLessonNeedsRoom = tutorSessionNeedsRoom(selectedTutorLessonOption?.sessionType);

  const canSubmitTutorBooking =
    !isBlock &&
    eventKind === 'tutor' &&
    Boolean(selectedTutorId && tutorLessonOptionId && selectedDate && selectedTime && selectedStudentId) &&
    tutorLessonOptions.length > 0 &&
    !hasConflict &&
    !isPastBooking &&
    !sessionsOverlap &&
    !submitting;

  const canSubmit =
    eventKind === 'room'
      ? isBlock
        ? canSubmitRoomBlock
        : canSubmitRoomBooking
      : isBlock
        ? canSubmitTutorBlock
        : canSubmitTutorBooking;

  const dialogTitle = isBlock
    ? t('spaceCalendar.createEvent', 'Block Time')
    : t('spaceCalendar.createManualBooking', 'Create Booking');

  const submitLabel = isBlock
    ? t('spaceCalendar.createEvent', 'Block Time')
    : t('spaceCalendar.createManualBooking', 'Create Booking');

  const clientLabel =
    eventKind === 'tutor'
      ? t('spaceCalendar.bookingStudentLabel', 'Student')
      : t('spaceCalendar.bookingRoomRenterLabel', 'Room renter');
  const unnamedClientLabel =
    eventKind === 'tutor'
      ? t('spaceCalendar.unnamedStudent', 'Student')
      : t('spaceCalendar.unnamedRoomRenter', 'Room renter');
  const emptyClientsHint =
    eventKind === 'tutor'
      ? t(
          'spaceCalendar.noStudentBookingClientsYet',
          'No students with lesson history yet. You can still create a guest booking.',
        )
      : t(
          'spaceCalendar.noRoomRenterBookingClientsYet',
          'No room renters with booking history yet. You can still create a guest booking.',
        );

  const bookingClientPicker = !isBlock ? (
    <>
      <BookingClientSelect
        label={clientLabel}
        unnamedLabel={unnamedClientLabel}
        clients={students}
        value={selectedStudentId}
        onChange={(next) => {
          setSelectedStudentId(next);
          if (!isManualBookingNewClient(next)) {
            setGuestPhoneLocal('');
          }
        }}
        loading={studentsLoading}
        sx={sx}
      />
      {!studentsLoading && students.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          {emptyClientsHint}
        </Alert>
      ) : null}
      {isManualBookingNewClient(selectedStudentId) ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr',
            alignItems: 'flex-start',
            gap: 1.5,
            width: '100%',
          }}
        >
          <TextField
            label={t('spaceCalendar.guestPhoneLabel', 'Guest phone (optional)')}
            value={guestPhoneLocal}
            onChange={(e) => {
              const value = e.target.value;
              if (new RegExp(`^\\d{0,${DEFAULT_MANUAL_BOOKING_LOCAL_LENGTH}}$`).test(value)) {
                setGuestPhoneLocal(value);
              }
            }}
            fullWidth
            sx={sx}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">{DEFAULT_MANUAL_BOOKING_COUNTRY_CODE}</InputAdornment>
              ),
            }}
          />
          <FormControlLabel
            sx={{
              m: 0,
              mt: 1,
              width: '100%',
              alignItems: 'center',
              '& .MuiFormControlLabel-label': {
                fontSize: '0.875rem',
                lineHeight: 1.3,
              },
            }}
            control={
              <Checkbox
                checked={sendBookingLinkSms}
                onChange={(e) => setSendBookingLinkSms(e.target.checked)}
              />
            }
            label={t('spaceCalendar.sendBookingLinkSms', 'Send booking link by SMS')}
          />
        </Box>
      ) : null}
    </>
  ) : null;

  const selectedRoomEntity = rooms.find((room) => room.id === selectedRoom) ?? null;
  const occurrenceCount = 1 + extraSessionStarts.length;
  const fallbackCurrency = currencyCode?.trim() || 'PLN';
  const bookingFieldsFilled =
    eventKind === 'room'
      ? Boolean(selectedRoom && selectedDate && selectedTime && selectedStudentId)
      : Boolean(
          selectedTutorId &&
            selectedTutorLessonOption &&
            selectedDate &&
            selectedTime &&
            selectedStudentId,
        );
  const priceSummary =
    isBlock || !bookingFieldsFilled
      ? null
      : eventKind === 'room' && selectedRoomEntity
        ? {
            lineLabel: selectedRoomEntity.name,
            sessionAmount: selectedRoomEntity.pricePerH * (bookingDuration / 60),
            durationMinutes: bookingDuration,
            currencyCode: fallbackCurrency,
          }
        : eventKind === 'tutor' && selectedTutorLessonOption
          ? {
              lineLabel: selectedTutorLessonOption.name,
              sessionAmount: selectedTutorLessonOption.price,
              durationMinutes: selectedTutorLessonOption.durationMinutes,
              currencyCode: selectedTutorLessonOption.lessonPriceCurrency?.trim() || fallbackCurrency,
            }
          : null;

  const durationSelect = (
    <FormControl fullWidth sx={sx}>
      <InputLabel>{t('spaceCalendar.duration')}</InputLabel>
      <Select
        value={bookingDuration}
        onChange={(e) => setBookingDuration(Number(e.target.value))}
        label={t('spaceCalendar.duration')}
      >
        {roomDurationOptions.map((mins) => (
          <MenuItem key={mins} value={mins}>
            {mins >= 60
              ? `${Math.floor(mins / 60)} ${
                  Math.floor(mins / 60) > 1 ? t('spaceCalendar.hours') : t('spaceCalendar.hour')
                }${mins % 60 ? ` ${mins % 60} ${t('spaceCalendar.min')}` : ''}`
              : `${mins} ${t('spaceCalendar.min')}`}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          margin: { xs: 1, sm: 2 },
          maxWidth: { xs: 'calc(100% - 16px)', sm: '600px' },
          boxShadow:
            mode === 'dark' ? '0px 8px 32px rgba(0, 0, 0, 0.6)' : '0px 4px 20px rgba(0, 0, 0, 0.12)',
          background: mode === 'dark' ? '#1F2129' : '#FFFFFF',
          border: `1px solid ${mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1.5,
          pt: 2.5,
          px: 3,
          '& .MuiTypography-root': {
            fontWeight: 700,
            fontSize: '1.35rem',
            color: mode === 'dark' ? '#ECECFB' : '#212529',
            letterSpacing: '-0.02em',
          },
        }}
      >
        {dialogTitle}
      </DialogTitle>
      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <DialogContent sx={{ pt: 3, px: 3, pb: 2 }}>
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={getCurrentLocale()}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, width: '100%' }}>
            {showEventKindToggle ? (
              <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={eventKind}
                  onChange={(_e, value: HostCreateEventKind | null) => {
                    if (value) setEventKind(value);
                  }}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1,
                    '& .MuiToggleButtonGroup-grouped': {
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: '8px !important',
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 1,
                    },
                  }}
                >
                  <ToggleButton value="room">
                    {isBlock
                      ? t('spaceCalendar.eventTypeRoom', 'Room block')
                      : t('spaceCalendar.bookingTypeRoom', 'Room booking')}
                  </ToggleButton>
                  <ToggleButton value="tutor">
                    {isBlock
                      ? t('spaceCalendar.eventTypeTutorBlock', 'Tutor block')
                      : t('spaceCalendar.eventTypeTutor', 'Tutor lesson')}
                  </ToggleButton>
                </ToggleButtonGroup>
            ) : null}

            {bookingClientPicker}

            {isBlock ? (
              <TextField
                label={t('spaceCalendar.blockTitleOptional', 'Title (optional)')}
                value={tutorTitle}
                onChange={(e) => setTutorTitle(e.target.value)}
                fullWidth
                sx={sx}
              />
            ) : null}

            {eventKind === 'room' ? (
              <>
                <FormControl fullWidth sx={sx}>
                  <InputLabel>{t('spaceCalendar.room')}</InputLabel>
                  <Select
                    value={selectedRoom}
                    onChange={(e) => setSelectedRoom(e.target.value)}
                    label={t('spaceCalendar.room')}
                  >
                    {rooms.map((room) => (
                      <MenuItem key={room.id} value={room.id}>
                        {room.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {durationSelect}
              </>
            ) : null}

            {eventKind === 'tutor' ? (
              <>
                <FormControl fullWidth sx={sx}>
                  <InputLabel>{t('spaceCalendar.tutorLabel', 'Tutor')}</InputLabel>
                  <Select
                    value={selectedTutorId}
                    onChange={(e) => setSelectedTutorId(e.target.value)}
                    label={t('spaceCalendar.tutorLabel', 'Tutor')}
                  >
                    {tutors.map((tutor) => (
                      <MenuItem key={tutor.tutorId} value={tutor.tutorId}>
                        {tutor.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {!isBlock ? (
                  <>
                    {tutorLessonOptions.length === 0 ? (
                      <Alert severity="warning" sx={{ borderRadius: 2 }}>
                        {t(
                          'tutorCalendar.walkInNoLessonOptions',
                          'This tutor has no lesson options yet. Edit their listing first.',
                        )}
                      </Alert>
                    ) : (
                      <TutorLessonOptionPicker
                        label={t('tutorCalendar.walkInLessonOption', 'Lesson option')}
                        options={tutorLessonOptions}
                        value={tutorLessonOptionId}
                        onChange={setTutorLessonOptionId}
                        sx={sx}
                      />
                    )}

                    {tutorLessonNeedsRoom ? (
                      rooms.length === 0 ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          {t(
                            'tutorCalendar.walkInNoRooms',
                            "No rooms available for this tutor's space. Create a room first.",
                          )}
                        </Alert>
                      ) : (
                        <FormControl fullWidth sx={sx}>
                          <InputLabel shrink>{t('spaceCalendar.room')}</InputLabel>
                          <Select
                            value={selectedRoom}
                            onChange={(e) => setSelectedRoom(e.target.value)}
                            label={t('spaceCalendar.room')}
                            displayEmpty
                            notched
                            renderValue={(value) => {
                              if (!value) {
                                return (
                                  <Typography component="span" variant="body2" color="text.secondary">
                                    {t(
                                      'hostTutorBookings.selectRoomPlaceholder',
                                      'Select a room',
                                    )}
                                  </Typography>
                                );
                              }
                              return rooms.find((room) => room.id === value)?.name || value;
                            }}
                          >
                            <MenuItem value="">
                              <Typography variant="body2" color="text.secondary">
                                {t('hostTutorBookings.clearRoom', 'Clear room')}
                              </Typography>
                            </MenuItem>
                            {rooms.map((room) => (
                              <MenuItem key={room.id} value={room.id}>
                                {room.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}

            {eventKind === 'tutor' && isBlock ? durationSelect : null}

            {sessionStarts.length > 1 ? (
              <BookingOccurrenceEditor
                starts={sessionStarts}
                durationMinutes={sessionDurationMinutes}
                minutesStep={startTimeMinutesStep}
                disabled={submitting}
                onChange={applySessionStarts}
              />
            ) : (
              <>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: 1.5,
                    width: '100%',
                  }}
                >
                  <DatePicker
                    label={t('spaceCalendar.date')}
                    value={selectedDate}
                    onChange={setSelectedDate}
                    disablePast
                    slotProps={{ textField: { fullWidth: true, sx } }}
                  />

                  <TimePicker
                    label={t('spaceCalendar.time')}
                    value={selectedTime}
                    onChange={setSelectedTime}
                    ampm={false}
                    minutesStep={startTimeMinutesStep}
                    slotProps={{ textField: { fullWidth: true, sx } }}
                  />
                </Box>
                {!isBlock && primaryStart ? (
                  <BookingOccurrenceEditor
                    starts={sessionStarts}
                    durationMinutes={sessionDurationMinutes}
                    minutesStep={startTimeMinutesStep}
                    disabled={submitting}
                    onChange={applySessionStarts}
                  />
                ) : null}
              </>
            )}

            {isPastBooking && (
              <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
                {t('spaceCalendar.pastBookingWarning')}
              </Alert>
            )}

            {hasConflict && !isPastBooking && (
              <Alert severity="error" sx={{ borderRadius: 2, fontWeight: 500 }}>
                {eventKind === 'room'
                  ? t('spaceCalendar.slotConflictWarning')
                  : t(
                      'tutorCalendar.slotConflictWarning',
                      'This slot conflicts with an existing lesson or block.',
                    )}
              </Alert>
            )}

            {/* Additional gear picker temporarily hidden for manual room bookings. */}
            {false && !isBlock && eventKind === 'room' ? (
              <>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: mode === 'dark' ? '#ECECFB' : '#212529',
                    mt: 1,
                  }}
                >
                  {t('spaceCalendar.additionalGear')}
                </Typography>

                <Box
                  sx={{
                    minHeight: '200px',
                    width: '100%',
                    border: `1px solid ${
                      mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'
                    }`,
                    borderRadius: 2,
                    p: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8f9fa',
                  }}
                >
                  <GearList
                    availableGears={availableGears}
                    selectedAdditionalGears={selectedAdditionalGears}
                    loadingGears={loadingGears}
                    gearError={gearError}
                    selectedRoom={selectedRoom}
                    onToggleGear={onToggleGear}
                  />
                </Box>
              </>
            ) : null}

            {priceSummary ? (
              <ManualBookingPriceSummary
                lineLabel={priceSummary.lineLabel}
                sessionAmount={priceSummary.sessionAmount}
                durationMinutes={priceSummary.durationMinutes}
                occurrenceCount={occurrenceCount}
                currencyCode={priceSummary.currencyCode}
                discountSettings={discountSettings}
              />
            ) : null}

            {isBlock ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t(
                  'spaceCalendar.blockEventHint',
                  'Creates a calendar block only — no booking, payment, or student record.',
                )}
              </Alert>
            ) : eventKind === 'tutor' ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t(
                  'spaceCalendar.tutorEventHint',
                  'Creates a confirmed tutor lesson. Pick a student from your list, or leave as walk-in. Choose the lesson option, and optionally a room for in-person lessons.',
                )}
              </Alert>
            ) : (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                {t(
                  'spaceCalendar.roomBookingHint',
                  'Creates a confirmed room booking (cash). Pick a client or add a guest phone for an SMS link.',
                )}
              </Alert>
            )}
          </Box>
        </LocalizationProvider>
      </DialogContent>
      <Divider
        sx={{
          mx: 3,
          borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
        }}
      />
      <DialogActions sx={{ p: 3, gap: 1.5 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            borderColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : '#4E67EB',
            color: mode === 'dark' ? '#ECECFB' : '#4E67EB',
          }}
        >
          {t('common.cancel')}
        </Button>
        <Button
          onClick={handleCreateBooking}
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          sx={{
            borderRadius: 2,
            textTransform: 'uppercase',
            fontWeight: 600,
            backgroundColor: '#4E67EB',
            color: '#ffffff',
            '&.Mui-disabled': {
              backgroundColor: mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)',
              color: mode === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.26)',
            },
          }}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
